import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import requestClient from '../../../utils/requestClient';
import FormInput from '../../../components/Elements/Form/FormInput';
import SuccessMessage from '../../../components/Elements/SuccessMessage';
import ErrorMessage from '../../../components/Elements/ErrorMessage';

const UserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedUserType, setSelectedUserType] = useState('');
  
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);

  useEffect(() => {
    async function getUser() {
      try {
        const response = await requestClient().get(`/api/v2/employer/employees/${id}/`);
        setUser(response.data.data);
        setSelectedLocation(response.data.data.location?.id || '');
        setSelectedDepartment(response.data.data.department?.id || '');
        setSelectedUserType(response.data.data.user_type || '');
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        setErrors(['Failed to load user data']);
        setLoading(false);
      }
    }
    getUser();
  }, [id]);

  // Fetch locations and departments when component mounts
  useEffect(() => {
    async function fetchCompanyData() {
      try {
        const response = await requestClient().get('/api/v2/employer/company/');
        if (response.data?.data) {
          setLocations(response.data.data.locations || []);
          setDepartments(response.data.data.departments || []);
        }
      } catch (error) {
        console.error('Failed to fetch company data:', error);
      }
    }
    fetchCompanyData();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess(false);
    setErrors(null);
    setIsSaving(true);
    try {
      const formData = {
        first_name: firstNameRef.current.value,
        last_name: lastNameRef.current.value,
        location: selectedLocation ? parseInt(selectedLocation) : null,
        department: selectedDepartment ? parseInt(selectedDepartment) : null,
        user_type: selectedUserType || null
      };
      await requestClient().patch(`/api/v2/employer/employees/${id}/`, formData);
      setSuccess('User details updated successfully');
      setIsSaving(false);
    } catch (error) {
      setErrors(error.response?.data?.error || ['Failed to update user']);
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    setIsDeleting(true);
    
    try {
      await requestClient().delete(`/api/v2/employer/employees/${id}/`);
      
      // Navigate to users list with success message
      navigate('/employer/users', { 
        state: { 
          successMessage: 'User deleted successfully' 
        } 
      });
    } catch (error) {
      console.error('Failed to delete user:', error);
      setErrors(['Failed to delete user. Please try again.']);
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <div>User not found</div>;
  }

  return (
    <main>
      <section className="bg-white">
        <div className='container py-8 lg:py-12'>
          <div className='border-b pb-8 flex lg:items-center lg:justify-between mb-8 gap-4 flex-col lg:flex-row'>
            <div>
              <nav className='hidden lg:block mb-4'>
                <ul className='text-sm uppercase flex items-center'>
                  <li>
                    <Link to="/employer/users">Users</Link>
                    <span className='mx-2'>/</span>
                  </li>
                  <li className='text-purple font-bold'>
                    {user.email}
                  </li>
                </ul>
              </nav>
              <h1>{user.first_name} {user.last_name}</h1>
            </div>
            <Link to="/employer/users" className="block lg:hidden">← USERS</Link>
            <button
                  type="button"
                  className='btn btn-red'
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
          {success ? <SuccessMessage title="Success" text={success} className="mb-4" /> : null}
          {errors ? <ErrorMessage title="Something is not right" errors={errors} className="mb-4" /> : null}
          <div className='max-w-[600px]'>
            <form onSubmit={handleSubmit} className='grid gap-4 mb-6'>
              <div>
                <label className='block text-sm font-bold mb-2'>Email</label>
                <input
                  type="email"
                  name="email"
                  value={user.email}
                  disabled
                  className='w-full px-4 py-2 border rounded-full bg-gray-100 cursor-not-allowed'
                />
                <p className='text-xs text-gray-700 mt-1'>Email cannot be changed</p>
              </div>
              <FormInput
                label="First Name*"
                type="text"
                name="first_name"
                inputRef={firstNameRef}
                value={user.first_name || ''}
                props={{ 'required': 'required' }}
              />
              <FormInput
                label="Last Name*"
                type="text"
                name="last_name"
                inputRef={lastNameRef}
                value={user.last_name || ''}
                props={{ 'required': 'required' }}
              />
              <div>
                <label className='block text-sm font-bold mb-2'>User Type</label>
                <select
                  name="user_type"
                  value={selectedUserType}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                  className='w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple'
                >
                  <option value="">Select a user type</option>
                  <option value="employee">Employee</option>
                  <option value="employer">Employer</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-bold mb-2'>Location</label>
                <select
                  name="location"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className='w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple'
                >
                  <option value="">Select a location</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className='block text-sm font-bold mb-2'>Department</label>
                <select
                  name="department"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className='w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple'
                >
                  <option value="">Select a department</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className='flex gap-4 mt-6'>
                <button
                  type="submit"
                  className='btn btn-purple'
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default UserEdit;